import React, { Dispatch, SetStateAction, useState } from 'react';
import { Col, Row } from '../../../lib/utils';
import { Body3Light, CaptionRegular, Head4 } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import type { ReorderNotice, SortType } from '../ClassPassPage';
import RoundedDropDown from '../../../components/DropDown/RoundedDropDown';
import ResetButton from '../../../components/Button/ResetButton';
import MembershipTemplate from './MembershipTemplate';
import { I_ALERT, I_CIRCLE_QUESTION, I_EMPTY_V2 } from '../../../types/images';
import { CustomCheckbox } from '../../../components/Grid/Grid';
import { DEFAULT_SECOND_SORT_TYPE, FIRST_SORT_OPTIONS, SECOND_SORT_OPTIONS } from '../sortOptions';
import styled from 'styled-components';
import { ArrowUpDown } from 'lucide-react';

// 옆의 RoundedDropDown(small) 과 같은 결: 높이 32, 알약형 24px 라운드, #d4dbe8 테두리, #fdfeff 바탕.
// 아이콘만 포인트 색(BLUE_400)으로 두고 글자는 드롭다운 라벨과 같은 #4a4a4a 를 쓴다.
const ReorderButton = styled.button<{ $disabled: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px 0 12px;
    border-radius: 24px;
    border: 1px solid #d4dbe8;
    background-color: ${({ $disabled }) => ($disabled ? '#f5f9ff' : '#fdfeff')};
    color: ${({ $disabled }) => ($disabled ? '#a3adbb' : '#4a4a4a')};
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
        background-color: ${({ $disabled }) => ($disabled ? '#f5f9ff' : '#f0f6ff')};
        border-color: ${({ $disabled }) => ($disabled ? '#d4dbe8' : '#b9ccec')};
    }
    &:focus-visible {
        outline: 2px solid #648fe4;
        outline-offset: 2px;
    }
`;

const firstOptions: SortType[] = FIRST_SORT_OPTIONS;
const secondOptions: SortType[] = SECOND_SORT_OPTIONS;

type Props = {
    firstSortType: SortType;
    setFirstSortType: Dispatch<SetStateAction<SortType>>;
    secondSortType: SortType;
    setSecondSortType: Dispatch<SetStateAction<SortType>>;
    filterMembershipTemplates: any;
    onClickDetailPage: (item: any) => void;
    isHideCoach: boolean;
    setIsHideCoach: any;
    onClickHideCoach?: () => void;
    onClickReorder?: () => void;
    // 테스트 계정(목록 조회 비활성)이거나 상품이 2개 미만이면 순서를 바꿀 게 없다
    reorderDisabled?: boolean;
    reorderNotice?: ReorderNotice | null;
    onDismissReorderNotice?: () => void;
};

const ClassPassList = ({
    firstSortType,
    setFirstSortType,
    secondSortType,
    setSecondSortType,
    filterMembershipTemplates,
    onClickDetailPage,
    isHideCoach,
    setIsHideCoach,
    onClickHideCoach,
    onClickReorder,
    reorderDisabled = false,
    reorderNotice = null,
    onDismissReorderNotice,
}: Props) => {
    const [isOpenFirst, setIsOpenFirst] = useState<boolean>(false);
    const [isOpenSecond, setIsOpenSecond] = useState<boolean>(false);

    // 필터 초기화 버튼 클릭이벤트
    const onClickFilterReset = () => {
        setFirstSortType({ value: 'ENTIRE', label: '전체 보기' });
        localStorage.setItem('firstSortType', JSON.stringify({ value: 'ENTIRE', label: '전체 보기' }));

        setSecondSortType(DEFAULT_SECOND_SORT_TYPE);
        localStorage.setItem('secondSortType', JSON.stringify(DEFAULT_SECOND_SORT_TYPE));

        setIsHideCoach(false);
        localStorage.setItem('isHideCoach', JSON.stringify(false));
    };

    const toggleFirst = () => {
        setIsOpenFirst(!isOpenFirst);
    };
    const handleFirstSelect = (option: any) => {
        setFirstSortType(option);
        setIsOpenFirst(false);
        localStorage.setItem('firstSortType', JSON.stringify(option));
    };

    const toggleSecond = () => {
        setIsOpenSecond(!isOpenSecond);
    };
    const handleSecondSelect = (option: any) => {
        setSecondSortType(option);
        setIsOpenSecond(false);
        localStorage.setItem('secondSortType', JSON.stringify(option));
    };

    return (
        <>
            <Row
                style={{
                    width: 1192,
                    justifyContent: 'space-between',
                    marginTop: 48,
                    alignItems: 'center',
                }}
            >
                <Row>
                    <Row
                        style={{ cursor: 'pointer', height: 24, alignItems: 'center' }}
                        onClick={() => {
                            window.open('https://mustmove.notion.site/ebd/bace33141cdc49799eba3ea109512ec3', '_blank'); // 새 탭에서 링크 열기
                        }}
                    >
                        <Head4 style={{ color: colors.LAVEL_4 }}>수강권 관리</Head4>{' '}
                        <img
                            src={I_CIRCLE_QUESTION}
                            style={{ width: 16, height: 16, marginLeft: 16, marginRight: 3 }}
                        />
                        <CaptionRegular style={{ color: colors.POINT_1, cursor: 'pointer' }}>
                            수강권 기능 알아보기
                        </CaptionRegular>
                    </Row>
                </Row>

                <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    {onClickReorder && (
                        // native disabled 대신 aria-disabled: 모달이 닫힐 때 이 버튼으로 포커스가 돌아와야 하는데,
                        // 저장 직후 목록 재조회(isFetching) 중이라 disabled 면 포커스가 body 로 떨어진다.
                        <ReorderButton
                            type="button"
                            className="mr-[16px]"
                            $disabled={reorderDisabled}
                            aria-disabled={reorderDisabled}
                            onClick={() => {
                                if (reorderDisabled) return;
                                onClickReorder();
                            }}
                            title={
                                reorderDisabled
                                    ? '순서를 바꿀 수강권이 2개 이상일 때 쓸 수 있어요'
                                    : '수강권이 보이는 순서를 드래그로 바꿔요'
                            }
                        >
                            <ArrowUpDown
                                size={15}
                                strokeWidth={2}
                                color={reorderDisabled ? '#a3adbb' : colors.BLUE_400}
                                aria-hidden="true"
                            />
                            <CaptionRegular style={{ color: 'inherit' }}>순서 변경</CaptionRegular>
                        </ReorderButton>
                    )}
                    <Row className=" cursor-pointer mr-[16px]" onClick={() => onClickHideCoach && onClickHideCoach()}>
                        <CustomCheckbox
                            checked={isHideCoach}
                            className="mr-[8px]"
                            onChange={() => onClickHideCoach && onClickHideCoach()}
                        />
                        <CaptionRegular>강사 생성 숨기기</CaptionRegular>
                    </Row>
                    <RoundedDropDown
                        toggleSortDropDown={toggleFirst}
                        sortType={firstSortType}
                        isOpenSort={isOpenFirst}
                        sortOptions={firstOptions}
                        handleSortSelect={handleFirstSelect}
                        size="small"
                        className="mr-[16px]"
                    />

                    <RoundedDropDown
                        toggleSortDropDown={toggleSecond}
                        sortType={secondSortType}
                        isOpenSort={isOpenSecond}
                        sortOptions={secondOptions}
                        handleSortSelect={handleSecondSelect}
                        size="small"
                        className="mr-[16px]"
                    />

                    <ResetButton text="초기화" callBack={onClickFilterReset} />
                </Row>
            </Row>

            {/* 순서 저장 결과 (인라인). 모달 위에 알럿을 겹치지 않는다.
                라이브 리전은 비어 있어도 항상 렌더한다 — 스크린리더는 이미 있는 리전의 내용 변화를 낭독하지,
                내용이 채워진 채 새로 생긴 요소는 안정적으로 낭독하지 않는다(WAI-ARIA 권고). */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{ width: 1192 - 24, marginLeft: 24, marginTop: reorderNotice ? 20 : 0 }}
            >
                {reorderNotice && (
                    <Row
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: `1px solid ${reorderNotice.tone === 'error' ? '#FCA5A5' : '#BFDBFE'}`,
                            backgroundColor: reorderNotice.tone === 'error' ? '#FEF2F2' : '#EFF6FF',
                        }}
                    >
                        <CaptionRegular style={{ color: reorderNotice.tone === 'error' ? '#991B1B' : '#1E40AF' }}>
                            {reorderNotice.message}
                        </CaptionRegular>
                        {onDismissReorderNotice && (
                            <button
                                type="button"
                                onClick={onDismissReorderNotice}
                                aria-label="안내 닫기"
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: reorderNotice.tone === 'error' ? '#991B1B' : '#1E40AF',
                                    fontSize: 14,
                                    lineHeight: 1,
                                    padding: '2px 4px',
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </Row>
                )}
            </div>

            <div
                style={{
                    width: 1192,
                    paddingLeft: 24,
                    marginTop: reorderNotice ? 20 : 40,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                }}
            >
                {/* 수강권 */}
                {filterMembershipTemplates === null || filterMembershipTemplates.length === 0 ? (
                    <Row
                        style={{
                            width: 1192,
                            paddingLeft: 24,
                            justifyContent: 'center',
                            marginTop: 152,
                        }}
                    >
                        <Col
                            style={{
                                width: 206,
                                height: 80,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <img src={I_EMPTY_V2} style={{ width: 24, height: 24 }} />
                            <Body3Light style={{ color: colors.LAVEL_2 }}>
                                {'사용할 수 있는 수강권이 없어요\n새로운 수강권을 만들어주세요!'}
                            </Body3Light>
                        </Col>
                    </Row>
                ) : (
                    filterMembershipTemplates.map((item: any, index: number) => {
                        return (
                            <MembershipTemplate
                                key={index + 'membershipTemplate'}
                                index={index}
                                item={item}
                                onClickDetailPage={onClickDetailPage}
                            />
                        );
                    })
                )}
            </div>
        </>
    );
};

export default ClassPassList;
